import { useEffect } from 'react';
import { getEnvironmentConfig } from 'utils/environment';
import { reportClientError } from './report-client-error';

function stringifyUnhandledReason(reason: unknown): string {
  if (reason instanceof Error) return reason.message;
  try {
    return JSON.stringify(reason);
  } catch {
    return String(reason);
  }
}

export function ClientErrorReportingSetup() {
  useEffect(() => {
    const { apiUrl: reportingApiUrl } = getEnvironmentConfig();
    if (!reportingApiUrl) return;

    const onError = (event: ErrorEvent) => {
      void reportClientError({
        message: event.message || 'Unhandled window.error',
        stack:
          event.error?.stack ??
          [event.filename, event.lineno, event.colno].filter(Boolean).join(':'),
      });
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      const { reason } = event;
      const messageBase =
        reason instanceof Error ? reason.message : stringifyUnhandledReason(reason);
      void reportClientError({
        message: `Unhandled promise rejection: ${messageBase}`,
        stack: reason instanceof Error ? reason.stack : null,
      });
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);

    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, []);

  return null;
}
